import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  ChevronRight, Minus, Plus, ShoppingBag, ArrowLeft,
  MapPin, Grape, Wine, FlaskConical, Thermometer, Utensils,
} from "lucide-react";
import { findWine, WINE_COLORS, winesByColor } from "@/data/wines";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import WineCard from "@/components/WineCard";

const WineDetail = () => {
  const { id } = useParams();
  const wine = findWine(id);
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [qty, setQty] = useState(1);

  if (!wine) {
    return (
      <div className="max-w-3xl mx-auto py-32 px-6 text-center" data-testid="wine-not-found">
        <p className="font-heading text-4xl text-[#1A1617]">Cuvée introuvable</p>
        <Link to="/vins" className="inline-flex items-center gap-2 mt-6 text-[#5B2333] border-b border-[#5B2333] pb-1 text-sm tracking-[0.18em] uppercase">
          <ArrowLeft className="w-4 h-4" /> Retour à la cave
        </Link>
      </div>
    );
  }

  const colorMeta = WINE_COLORS[wine.color];
  const related = winesByColor(wine.color).filter((w) => w.id !== wine.id).slice(0, 4);

  const rows = [
    { icon: MapPin,       label: "Origines",   value: wine.origin },
    { icon: Grape,        label: "Cépages",    value: wine.grape },
    wine.aging && { icon: FlaskConical, label: "Élevage", value: wine.aging },
    { icon: Wine,         label: "Appellation",value: `${wine.appellation} · ${wine.abv}` },
    { icon: Thermometer,  label: "Service",    value: wine.serving },
    { icon: Utensils,     label: "Accords",    value: wine.pairing },
  ].filter(Boolean);

  const handleAdd = () => {
    addItem(wine, qty);
    toast.success(`${qty} × ${wine.name} ajouté${qty > 1 ? "s" : ""} au panier`);
  };

  const handleBuyNow = () => {
    addItem(wine, qty);
    navigate("/panier");
  };

  return (
    <div data-testid="wine-detail-page" className="bg-[#F9F6F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 md:py-16">
        <nav className="flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.15em] uppercase text-[#5C5651] mb-8">
          <Link to="/" className="hover:text-[#5B2333]">Accueil</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to={`/vins?couleur=${wine.color}`} className="hover:text-[#5B2333]">{colorMeta.label}</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#1A1617] truncate max-w-[180px] sm:max-w-[280px]">{wine.name}</span>
        </nav>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-start">
          {/* Bottle photo — fully visible, no crop */}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <div className="relative bg-gradient-to-b from-white to-[#F0EBDC] border border-[#E8E2D2] h-[460px] sm:h-[560px] lg:h-[640px] flex items-center justify-center p-6 sm:p-8 overflow-hidden">
              <img
                src={wine.image}
                alt={wine.name}
                className="block max-h-full max-w-full w-auto h-auto object-contain drop-shadow-[0_25px_45px_rgba(91,35,51,0.30)]"
              />
              <span
                className="absolute top-5 left-5 text-[10px] tracking-[0.25em] uppercase font-semibold text-white px-3 py-1.5"
                style={{ background: colorMeta.accent }}
              >
                Vin {colorMeta.label.slice(0, -1).toLowerCase()}
              </span>
              {wine.format !== "75 cl" && (
                <span className="absolute top-5 right-5 text-[10px] tracking-[0.25em] uppercase font-semibold text-[#1A1617] bg-white/95 px-3 py-1.5 border border-[#E8E2D2]">
                  {wine.format}
                </span>
              )}
            </div>
          </div>

          {/* Info */}
          <div className="lg:col-span-7">
            <p className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-[#C5A059] font-semibold">
              {wine.producer} · {wine.region}
            </p>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-[#1A1617] mt-2 leading-tight uppercase tracking-[0.02em]">
              {wine.name.replace(` — ${wine.format}`, "")}
            </h1>
            <p className="text-xs tracking-[0.2em] uppercase text-[#5B2333] mt-2 font-semibold">
              {wine.appellation} · {wine.abv}{wine.format !== "75 cl" ? ` · ${wine.format}` : ""}
            </p>

            <p className="text-sm sm:text-base text-[#5C5651] mt-6 leading-relaxed">{wine.description}</p>

            {/* Detailed info grid — same structure as the PDF catalog */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6 mt-8 py-7 border-y border-[#E8E2D2]">
              {rows.map((r) => (
                <InfoLine key={r.label} icon={r.icon} label={r.label} value={r.value} fullWidth={r.label === "Accords"} />
              ))}
            </div>

            {/* Nose */}
            {wine.nose && (
              <div className="mt-7">
                <p className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-[#C5A059] font-semibold">Nez</p>
                <p className="text-sm sm:text-base text-[#1A1617] mt-2 italic leading-relaxed">{wine.nose}</p>
              </div>
            )}

            {/* Price + qty */}
            <div className="flex items-center justify-between mt-10">
              <div>
                <span data-testid="wine-detail-price" className="font-heading text-4xl sm:text-5xl text-[#5B2333]">
                  {wine.price.toFixed(2)} €
                </span>
                <span className="block text-xs tracking-[0.2em] uppercase text-[#5C5651] mt-1">
                  {wine.format}
                </span>
              </div>
              <div className="flex items-center border border-[#E8E2D2] bg-white">
                <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-decrement" className="w-10 h-12 flex items-center justify-center hover:bg-[#E8E2D2]/30 transition" aria-label="Diminuer la quantité">
                  <Minus className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <span data-testid="qty-value" className="w-12 text-center font-medium">{qty}</span>
                <button type="button" onClick={() => setQty(qty + 1)} data-testid="qty-increment" className="w-10 h-12 flex items-center justify-center hover:bg-[#E8E2D2]/30 transition" aria-label="Augmenter la quantité">
                  <Plus className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 sm:gap-4 mt-7">
              <button type="button" onClick={handleAdd} data-testid="detail-add-to-cart" className="inline-flex items-center gap-2 bg-[#5B2333] text-white px-6 sm:px-8 py-3.5 text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#4A1525] transition-colors">
                <ShoppingBag className="w-4 h-4" strokeWidth={1.8} />
                Ajouter au panier
              </button>
              <button type="button" onClick={handleBuyNow} data-testid="detail-buy-now" className="inline-flex items-center gap-2 bg-transparent border border-[#1A1617] text-[#1A1617] px-6 sm:px-8 py-3.5 text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1A1617] hover:text-[#F9F6F0] transition-all">
                Commander maintenant
              </button>
            </div>
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="mt-20 sm:mt-28">
            <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl text-[#1A1617] mb-8">
              Dans la même catégorie
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 lg:gap-6">
              {related.map((w) => <WineCard key={w.id} wine={w} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoLine = ({ icon: Icon, label, value, fullWidth }) => (
  <div className={fullWidth ? "sm:col-span-2" : ""}>
    <div className="flex items-center gap-3">
      <span className="w-10 h-10 bg-[#5B2333] text-[#F9F6F0] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" strokeWidth={1.6} />
      </span>
      <div className="flex-1">
        <p className="text-[10px] tracking-[0.25em] uppercase text-[#5B2333] font-bold">{label}</p>
        <p className="text-sm text-[#1A1617] mt-0.5 leading-snug">{value}</p>
      </div>
    </div>
  </div>
);

export default WineDetail;
